// Phase 5 — Карта станций.
//
// The map closes the original project scope — a geographical
// alternative to the textual station picker plus a region-level
// view of where data is good and where it isn't.
//
// This commit (C2) lands the base map only:
//   * Leaflet container + CartoDB Positron tiles (light parchment-
//     friendly basemap).
//   * Viewport persistence in localStorage so back-nav from
//     DetailScreen restores the same pan / zoom.
//   * «На активные станции» button that fits the bounding box of
//     stations with `total_jobs > 0`.
//
// Markers, bottom sheet and filters land in C3-C5.

import { useQueryClient } from '@tanstack/react-query';
import type { LatLngBoundsLiteral, Map as LeafletMap } from 'leaflet';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';

import type { MapStation } from '../api/types';
import { BatchModal } from '../components/BatchModal';
import {
  EMPTY_FILTERS,
  MapFilters,
  stationMatchesFilters,
  type MapFilterState,
} from '../components/map/MapFilters';
import { MapLegend } from '../components/map/MapLegend';
import { MapSelectionFooter } from '../components/map/MapSelectionFooter';
import { StationBottomSheet } from '../components/map/StationBottomSheet';
import { StationMarker } from '../components/map/StationMarker';
import { useMapStations } from '../hooks/useMapStations';
import { useTelegramWebApp } from '../hooks/useTelegramWebApp';

import './MapScreen.css';

/** Phase 5.1 — opt-in selection mode passed by BatchModal when the
 *  operator opens «выбрать на карте». Toggles the map's normal
 *  marker-click→bottom-sheet behaviour into a marker-click→toggle-
 *  in-set behaviour, and renders a fixed footer with «Готово /
 *  Отмена» actions instead of the bottom sheet. The
 *  `initialSelection` array seeds the in-memory `Set<string>` so
 *  codes already in BatchModal's textarea start ringed. */
export interface MapSelectionMode {
  initialSelection: string[];
  onConfirm: (codes: string[]) => void;
  onCancel: () => void;
}

/** Phase 5 — localStorage key for the persisted viewport.
 *  Follows the `gnss-qa:<name>` project convention (siblings:
 *  `gnss-qa:analytics-filters`, `gnss-qa:residual-mode`).
 *  Privacy-mode Telegram WebView throws on localStorage access;
 *  every touch is try/catch-guarded. */
const VIEWPORT_STORAGE_KEY = 'gnss-qa:map-viewport';

/** Default fallback viewport — used when the operator has never
 *  visited the map AND the active-stations bbox is empty (no data
 *  yet). Centred on lat 20° at zoom 2 so the whole globe is
 *  visible at once. */
const DEFAULT_VIEWPORT: ViewportState = {
  center: [20, 0],
  zoom: 2,
};

interface ViewportState {
  center: [number, number];
  zoom: number;
}

function _readViewport(): ViewportState | null {
  try {
    const raw = localStorage.getItem(VIEWPORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ViewportState>;
    // Defensive shape-check — operator could have hand-edited the
    // storage via devtools or upgraded from a stale format.
    if (
      !Array.isArray(parsed.center) ||
      parsed.center.length !== 2 ||
      typeof parsed.center[0] !== 'number' ||
      typeof parsed.center[1] !== 'number' ||
      typeof parsed.zoom !== 'number'
    ) {
      return null;
    }
    return {
      center: [parsed.center[0], parsed.center[1]],
      zoom: parsed.zoom,
    };
  } catch {
    return null;
  }
}

function _writeViewport(v: ViewportState): void {
  try {
    localStorage.setItem(VIEWPORT_STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* ignore storage failures in privacy-mode WebView */
  }
}

/** Geographic mean of stations with `total_jobs > 0`. Used as the
 *  initial centre when localStorage is empty — better starting
 *  point than the world map for an operator who has run data. */
function _activeStationsCenter(stations: MapStation[]): ViewportState | null {
  const active = stations.filter((s) => s.total_jobs > 0);
  if (active.length === 0) return null;
  const sumLat = active.reduce((acc, s) => acc + s.latitude, 0);
  const sumLng = active.reduce((acc, s) => acc + s.longitude, 0);
  return {
    center: [sumLat / active.length, sumLng / active.length],
    // Zoom 3 is a hemisphere-ish view — enough to see the spread
    // without zooming into one cluster. Operator can refine.
    zoom: 3,
  };
}

/** Bounding box of `total_jobs > 0` stations. Drives the «На
 *  активные станции» button. */
function _activeBounds(
  stations: MapStation[],
): LatLngBoundsLiteral | null {
  const active = stations.filter((s) => s.total_jobs > 0);
  if (active.length === 0) return null;
  let minLat = active[0]!.latitude;
  let maxLat = active[0]!.latitude;
  let minLng = active[0]!.longitude;
  let maxLng = active[0]!.longitude;
  for (const s of active) {
    if (s.latitude < minLat) minLat = s.latitude;
    if (s.latitude > maxLat) maxLat = s.latitude;
    if (s.longitude < minLng) minLng = s.longitude;
    if (s.longitude > maxLng) maxLng = s.longitude;
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}

/** Inner component that owns the Leaflet map handle. Watches pan /
 *  zoom and writes the new viewport to localStorage. */
function MapViewportTracker({
  onChange,
}: {
  onChange: (v: ViewportState) => void;
}) {
  const map = useMap();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const sync = () => {
      const c = map.getCenter();
      onChangeRef.current({ center: [c.lat, c.lng], zoom: map.getZoom() });
    };
    // Track *end* events only — Leaflet fires `move`/`zoom`
    // many times per gesture; we only need the final position.
    map.on('moveend', sync);
    map.on('zoomend', sync);
    return () => {
      map.off('moveend', sync);
      map.off('zoomend', sync);
    };
  }, [map]);

  return null;
}

/** Phase 5.2 — Leaflet 1.9.x ships the attribution control with a
 *  default prefix of `🇺🇦 Leaflet`. The UA-flag emoji injects a
 *  political message into every map view, which is not where the
 *  operator wants politics. Strip the emoji on mount; keep the
 *  product name «Leaflet» as the bare prefix (still credits the
 *  library, satisfies the goodwill expectation).
 *
 *  The CartoDB attribution itself (`<TileLayer attribution=…>`) is
 *  kept verbatim — that's the basemap licence requirement.
 *
 *  Implementation: imperative `setPrefix` via the `useMap` handle
 *  inside a no-render child component. Cleaner than disabling the
 *  default attribution control + remounting a custom
 *  `<AttributionControl>`, which would also strip the
 *  position/styling defaults. */
function AttributionPrefixFix() {
  const map = useMap();
  useEffect(() => {
    map.attributionControl.setPrefix('Leaflet');
  }, [map]);
  return null;
}

// Phase 6.9 C1 — the «Все на экран» fit-to-bounds control used to
// be a standalone top-right overlay (`FitActiveButton`, `useMap`-
// driven). It now lives INSIDE the collapsible MapFilters panel
// (spec §A.1) — the expanded panel was colliding with it. The
// button there can't use `useMap` (MapFilters is outside
// MapContainer), so MapScreen drives `fitBounds` through `mapRef`.

export function MapScreen({
  selectionMode,
}: { selectionMode?: MapSelectionMode } = {}) {
  const { data, isLoading, isError } = useMapStations();
  const qc = useQueryClient();
  const { tg } = useTelegramWebApp();

  // Phase 5 D — selected-station drives the bottom sheet visibility.
  // `null` = no sheet open. Disabled entirely in selectionMode —
  // marker clicks have a different meaning there.
  const [selected, setSelected] = useState<MapStation | null>(null);
  // BatchModal predfill triggered by the bottom sheet's «+ Запустить»
  // button. Snapshot pattern — copy the code into a local state so
  // re-clicks on different markers don't tear down the modal.
  const [batchPredfill, setBatchPredfill] = useState<string | null>(null);
  // Phase 5 C5 — filter state. Per spec §E.4, NOT persisted —
  // every map open starts with the default (no filters).
  const [filters, setFilters] = useState<MapFilterState>(EMPTY_FILTERS);
  // Phase 5.1 — selection set for «выбрать на карте» mode. Seeded
  // from `selectionMode.initialSelection` so codes already in
  // BatchModal's textarea start ringed. The lazy useState
  // initialiser reads the prop exactly once on mount — if the
  // BatchModal mutates `initialSelection` mid-picker (unlikely)
  // we don't re-sync, matching the snapshot pattern from
  // BatchModal.initialStationCodes.
  const [selection, setSelection] = useState<Set<string>>(
    () => new Set(selectionMode?.initialSelection ?? []),
  );

  // Phase 6.9 C1 — handle to the Leaflet map. The «Все на экран»
  // button now lives inside MapFilters (outside MapContainer), so
  // it can't use `useMap`; MapScreen drives fitBounds through this
  // ref instead. react-leaflet v4 forwards the L.Map instance here.
  const mapRef = useRef<LeafletMap | null>(null);

  const isSelectionMode = selectionMode !== undefined;

  // Compute the initial viewport once per data arrival. Three
  // priorities, highest first:
  //   1. Persisted localStorage from a previous session.
  //   2. Geographic mean of active stations from the fresh
  //      response.
  //   3. Hardcoded world-view fallback.
  const initialViewport = useMemo<ViewportState>(() => {
    const stored = _readViewport();
    if (stored !== null) return stored;
    if (data?.stations) {
      const computed = _activeStationsCenter(data.stations);
      if (computed !== null) return computed;
    }
    return DEFAULT_VIEWPORT;
  }, [data?.stations]);

  // Persist on every pan / zoom. Memo'd so the event handler
  // identity is stable across re-renders.
  const handleViewportChange = useMemo(
    () => (v: ViewportState) => _writeViewport(v),
    [],
  );

  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // C-MAP (B) — vertical-swipe lifecycle, scoped to MapScreen. Telegram vertical
  // swipes fight Leaflet gestures (a multi-touch pinch partially minimized the
  // Mini App). Disable them for exactly as long as the map is mounted, then
  // restore the PRIOR state. Feature-detected (older clients lack the API →
  // no-op) and NOT re-run on config / filter / sheet / status re-renders (deps
  // are just the stable `tg`). This effect is the sole swipe-policy owner.
  useEffect(() => {
    if (!tg?.disableVerticalSwipes) return;
    const prev = tg.isVerticalSwipesEnabled; // true | false | undefined
    tg.disableVerticalSwipes();
    return () => {
      // Restore: re-enable UNLESS swipes were EXPLICITLY off before. `false` →
      // leave off (respect a prior owner); `true` / `undefined` → restore the
      // Telegram default via enableVerticalSwipes (documented safe policy — this
      // effect is the only swipe-policy owner in the codebase).
      if (prev !== false) tg.enableVerticalSwipes?.();
    };
  }, [tg]);

  // C-MAP (A) — scoped Telegram BackButton for the station sheet: hardware Back
  // closes the sheet instead of minimizing the Mini App. Shown only while the
  // sheet is open; one stable handler; offClick(same ref) + hide on close. No
  // global hook; no conflict with DetailScreen's BackButton (a different route).
  const sheetOpen = !isSelectionMode && selected !== null;
  useEffect(() => {
    if (!sheetOpen || !tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => setSelected(null);
    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton?.offClick(onBack);
      tg.BackButton?.hide();
    };
  }, [sheetOpen, tg]);

  // Loading shimmer mirrors AnalyticsScreen's empty-state plate so
  // the operator gets consistent visual feedback across screens.
  if (isLoading) {
    return (
      <div className="map-screen screen">
        <div className="analytics-empty">загрузка станций…</div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="map-screen screen">
        <div className="analytics-error">не удалось загрузить станции</div>
      </div>
    );
  }

  const fitBounds = _activeBounds(data.stations);

  // Phase 6.9 C1 — «Все на экран» handler, invoked by the button
  // inside MapFilters. Fits the active-stations bbox via the map
  // handle. No-op if the map isn't mounted yet or there's nothing
  // to fit to.
  const handleFitActive = () => {
    if (mapRef.current !== null && fitBounds !== null) {
      mapRef.current.fitBounds(fitBounds, { padding: [20, 20] });
    }
  };

  // Phase 5 C5 — pre-compute the visible-station list for the
  // counter chips. Keep it as one filter pass so the chips and
  // the per-marker opacity decision can't drift.
  const visibleStations = data.stations.filter((s) =>
    stationMatchesFilters(s, filters),
  );
  const visibleSet = new Set(visibleStations.map((s) => s.code));

  return (
    <div className="map-screen screen" data-testid="map-screen">
      <MapFilters
        filters={filters}
        onChange={setFilters}
        visibleStations={visibleStations}
        onFitActive={handleFitActive}
        canFit={fitBounds !== null}
      />
      {/* Mount Leaflet only after the first React effect so SSR /
          dev-server hot-reload don't double-initialise the map
          container. */}
      {hasMounted && (
        <MapContainer
          ref={mapRef}
          center={initialViewport.center}
          zoom={initialViewport.zoom}
          className="map-screen-container"
          // Telegram WebView is mobile-only — disable mouse-wheel
          // zoom (covered by pinch) so a stray scroll over the map
          // doesn't yank the viewport away from the operator.
          scrollWheelZoom={false}
          worldCopyJump={true}
        >
          <TileLayer
            // CartoDB Positron — light monochromatic basemap that
            // sits well with the parchment design system. Requires
            // attribution per the Tile Usage Policy.
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://carto.com/attributions">CartoDB</a>'
            subdomains={['a', 'b', 'c', 'd']}
          />
          <MapViewportTracker onChange={handleViewportChange} />
          <AttributionPrefixFix />
          {/* Phase 5 C3 + D + C5 + 5.1 — markers route through one
              of two click handlers depending on mode:
              * normal — `setSelected` opens the bottom sheet;
              * selection-mode — toggle the station's code in the
                selection set. */}
          {data.stations.map((s) => (
            <StationMarker
              key={s.code}
              station={s}
              isSelected={isSelectionMode && selection.has(s.code)}
              onClick={(st) => {
                if (isSelectionMode) {
                  setSelection((prev) => {
                    const next = new Set(prev);
                    if (next.has(st.code)) next.delete(st.code);
                    else next.add(st.code);
                    return next;
                  });
                } else {
                  setSelected(st);
                }
              }}
              opacityMultiplier={visibleSet.has(s.code) ? 1 : 0}
              selectedConfigs={filters.configs}
            />
          ))}
        </MapContainer>
      )}

      {/* C7a — context-aware colour legend. Shown in the normal map view (not
          the «выбрать на карте» selection mode) and hidden while the station
          sheet is open so it never overlaps it. `perConfig` follows the marker
          recolor mode (filters.configs) so the grey swatch never carries two
          meanings on screen at once. */}
      {!isSelectionMode && selected === null && (
        <MapLegend perConfig={filters.configs.length > 0} />
      )}

      {/* Phase 5 D — bottom sheet. Mounted only when a station is
          selected AND we're not in selection-mode. Selection-mode
          uses the marker click for a different purpose, so the
          bottom sheet stays hidden there. */}
      {!isSelectionMode && selected !== null && (
        <StationBottomSheet
          station={selected}
          onClose={() => setSelected(null)}
          onRunBatch={(code) => {
            // Close the sheet, then open BatchModal predfilled.
            setSelected(null);
            setBatchPredfill(code);
          }}
        />
      )}

      {/* Phase 5.1 — selection footer for «выбрать на карте». */}
      {isSelectionMode && (
        <MapSelectionFooter
          selected={Array.from(selection).sort()}
          onConfirm={() =>
            selectionMode!.onConfirm(Array.from(selection).sort())
          }
          onCancel={() => selectionMode!.onCancel()}
        />
      )}

      {/* Phase 5 D — BatchModal driven by the bottom sheet's
          «+ Запустить серию для X» button. Mounted only when a
          predfill code is set so the modal's initialStationCodes
          snapshot fires fresh per open. */}
      {batchPredfill !== null && (
        <BatchModal
          open={true}
          initialStationCodes={[batchPredfill]}
          onClose={() => setBatchPredfill(null)}
          onCreated={() => {
            setBatchPredfill(null);
            void qc.invalidateQueries({ queryKey: ['jobs'] });
            void qc.invalidateQueries({ queryKey: ['status'] });
            void qc.invalidateQueries({ queryKey: ['batches'] });
          }}
        />
      )}
    </div>
  );
}
